import { z } from 'zod';
import { PapelSchema } from '../comum/papeis';

/** Senha do painel: público pequeno, então exigimos comprimento em vez de charadas. */
export const SenhaSchema = z
  .string()
  .min(12, 'a senha precisa de pelo menos 12 caracteres')
  .max(200);

export const EmailSchema = z.string().email('e-mail inválido').max(200).toLowerCase().trim();

export const LoginSchema = z.object({
  email: EmailSchema,
  senha: z.string().min(1).max(200),
});
export type Login = z.infer<typeof LoginSchema>;

/** Resposta do login: nunca devolve token final antes do TOTP. */
export const RespostaLoginSchema = z.object({
  desafioTotp: z.string().min(10),
  precisaAtivarTotp: z.boolean(),
});
export type RespostaLogin = z.infer<typeof RespostaLoginSchema>;

export const CodigoTotpSchema = z
  .string()
  .regex(/^[0-9]{6}$/, 'o código do aplicativo tem 6 dígitos');

export const TotpDesafioSchema = z.object({
  desafioTotp: z.string().min(10),
  codigo: CodigoTotpSchema,
});
export type TotpDesafio = z.infer<typeof TotpDesafioSchema>;

export const TotpAtivarSchema = z.object({
  desafioTotp: z.string().min(10),
});
export type TotpAtivar = z.infer<typeof TotpAtivarSchema>;

export const TotpConfirmarSchema = z.object({
  desafioTotp: z.string().min(10),
  codigo: CodigoTotpSchema,
});
export type TotpConfirmar = z.infer<typeof TotpConfirmarSchema>;

export const RespostaTotpAtivarSchema = z.object({
  otpauth: z.string().startsWith('otpauth://'),
  segredoFormatado: z.string().min(16),
});
export type RespostaTotpAtivar = z.infer<typeof RespostaTotpAtivarSchema>;

export const CodigoRecuperacaoSchema = z.object({
  desafioTotp: z.string().min(10),
  codigo: z.string().min(8).max(64),
});
export type CodigoRecuperacao = z.infer<typeof CodigoRecuperacaoSchema>;

/** Pedido de redefinição de senha por e-mail. A resposta é 200 sempre. */
export const EsquecerSenhaSchema = z.object({
  email: EmailSchema,
});
export type EsquecerSenha = z.infer<typeof EsquecerSenhaSchema>;

/** O token claro veio do link do e-mail; o banco só conhece o hash dele. */
export const RedefinirSenhaSchema = z.object({
  token: z.string().min(20).max(200),
  novaSenha: SenhaSchema,
});
export type RedefinirSenha = z.infer<typeof RedefinirSenhaSchema>;

export const SessaoAbertaSchema = z.object({
  acesso: z.string().min(20),
  expiraEm: z.iso.datetime(),
  usuario: z.object({
    id: z.string(),
    email: z.string(),
    nome: z.string(),
    papel: PapelSchema,
  }),
  codigosRecuperacao: z.array(z.string()).optional(),
});
export type SessaoAberta = z.infer<typeof SessaoAbertaSchema>;

export const DURACAO_ACESSO_MIN = 15;
export const DURACAO_REFRESH_DIAS = 7;
/**
 * Quanto tempo depois de girar um refresh ainda vale reapresentá-lo.
 *
 * Não é frouxidão: é a diferença entre uma corrida e um roubo. Quando a tela
 * do aparelho acorda, várias consultas tomam 401 juntas e pedem a renovação
 * com o mesmo cookie — o único que o navegador tem. Dentro desta janela, quem
 * perde a corrida ouve "tente de novo"; fora dela, é cópia, e a família cai.
 *
 * Trinta segundos cobrem com folga uma rajada de rede ruim e não dão nada a
 * um ladrão: ele continua sem receber token nenhum, só adia a detecção.
 */
export const TOLERANCIA_ROTACAO_S = 30;
/**
 * Janela entre a senha e o segundo fator.
 *
 * Eram **2 minutos**, e 2 minutos é a conta de quem já tem o aplicativo aberto
 * com a entrada pronta. A PRIMEIRA vez não é essa conta: é destravar o celular,
 * achar o aplicativo, criar a entrada, enquadrar o QR e digitar seis dígitos —
 * com as mãos em dois aparelhos. Estourado o prazo, a tela dizia "Não foi
 * possível completar a operação" e ficava exibindo um QR que já não valia.
 *
 * Dez minutos não afrouxam o segundo fator: o desafio não dá acesso a nada
 * sozinho, e continua exigindo o código do aplicativo. O que ele encurtava era
 * só a paciência de quem está cadastrando.
 */
export const DURACAO_DESAFIO_TOTP_MIN = 10;
export const QUANTIDADE_CODIGOS_RECUPERACAO = 10;
/** Quanto tempo o link de redefinição de senha vale depois de pedido. */
export const DURACAO_REDEFINICAO_MIN = 30;
